// Master Apps · QueryClient único do app (defaults documentados em
// docs/tanstack-query.md).
//
// Política de retentativa (docs/rate-limiting.md): erro de cliente (4xx) não se
// resolve repetindo o mesmo request, então não repetimos — a exceção é o 429
// RATE_LIMIT, que é temporário e vem com o tempo de espera. Falha de rede e 5xx
// mantêm a única retentativa que já era o default do repo (`retry: 1`).
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/hooks/use-applications';

/** Teto do backoff exponencial (falha de rede / 5xx). */
const MAX_BACKOFF_MS = 30_000;
/**
 * Espera máxima que aceitamos cumprir automaticamente num 429. Se o servidor
 * pedir MAIS do que isso, não repetimos: prender a tela em "carregando" por um
 * minuto é pior do que falhar na hora, porque o cartão de erro (ScreenError já
 * lê `details.retryAfterSec`) informa quantos segundos faltam e o usuário decide
 * quando tentar de novo.
 */
const RATE_LIMIT_AUTO_WAIT_CAP_MS = 10_000;
/** Quantas vezes repetir um 429 (com a espera pedida pelo servidor no meio). */
const RATE_LIMIT_MAX_RETRIES = 2;
/** Retentativas de falha transitória (rede/5xx) — o `retry: 1` original. */
const TRANSIENT_MAX_RETRIES = 1;

/**
 * 429 do rate limiting da API. O envelope traz `code: 'RATE_LIMIT'`; o status é
 * o critério principal porque outros 429 de negócio (ex.: ONBOARDING_LOCKED)
 * também são "espere e tente de novo" — e nunca chegam aqui, porque quem os
 * dispara é uma mutation (que não repete sozinha).
 */
function isRateLimit(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 429 || error.code === 'RATE_LIMIT');
}

/**
 * Espera pedida pelo servidor num 429, em ms (o contrato manda SEGUNDOS, tanto
 * em `details.retryAfterSec` quanto no header `Retry-After`). Aqui só dá para
 * ler o envelope: `ApiError` carrega status/code/details, não os headers — quem
 * chama a API por `fetch` copia o header para `details.retryAfterSec` antes de
 * lançar o erro (é o que o hook de favoritos faz).
 */
export function rateLimitWaitMs(error: unknown): number | undefined {
  if (!isRateLimit(error)) return undefined;
  const sec = error.details?.retryAfterSec;
  if (typeof sec !== 'number' || !Number.isFinite(sec) || sec <= 0) return undefined;
  return Math.ceil(sec * 1000);
}

/**
 * O 429 vale retentativa automática? Sim quando o servidor não disse o tempo
 * (aí usamos backoff) ou quando o tempo pedido é curto. Não quando a espera é
 * longa — nesse caso é o usuário que decide, no botão "Tentar novamente".
 */
function shouldWaitOutRateLimit(error: unknown): boolean {
  if (!isRateLimit(error)) return false;
  const wait = rateLimitWaitMs(error);
  return wait === undefined || wait <= RATE_LIMIT_AUTO_WAIT_CAP_MS;
}

/** Espera entre tentativas: honra o 429; senão, backoff exponencial com teto. */
export function retryDelay(attemptIndex: number, error: unknown): number {
  return rateLimitWaitMs(error) ?? Math.min(1000 * 2 ** attemptIndex, MAX_BACKOFF_MS);
}

/**
 * Queries: 429 repete até duas vezes (respeitando o tempo pedido, se for curto),
 * qualquer outro 4xx não repete (400/401/403/404 não mudam de resposta ao serem
 * repetidos), e o resto (rede, 5xx) repete uma vez. Nunca é infinito: os dois
 * limites são constantes deste arquivo.
 */
export function retryQuery(failureCount: number, error: unknown): boolean {
  if (isRateLimit(error)) {
    return shouldWaitOutRateLimit(error) && failureCount < RATE_LIMIT_MAX_RETRIES;
  }
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < TRANSIENT_MAX_RETRIES;
}

/**
 * Mutation IDEMPOTENTE (ex.: PUT/DELETE de favorito, em que repetir produz o
 * mesmo estado): repete SÓ em 429, com a mesma regra de espera curta. Escrita não
 * idempotente (POST de cadastro, onboarding) continua no default `retry: 0` —
 * repetir duplicaria registro.
 */
export function retryIdempotentMutation(failureCount: number, error: unknown): boolean {
  return shouldWaitOutRateLimit(error) && failureCount < RATE_LIMIT_MAX_RETRIES;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      retry: retryQuery,
      retryDelay,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Nenhuma escrita repete por padrão; quem é idempotente opta explicitamente
      // (retry: retryIdempotentMutation no próprio hook).
      retry: 0,
    },
  },
});
